import {
  faAdd,
  faCheckCircle,
  faClone,
  faDollarSign,
  faEdit,
  faExclamationCircle,
  faFileExcel,
  faMoneyBillWheat,
  faSearch,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  addMonths,
  endOfMonth,
  format,
  setDate,
  setMonth,
  setYear,
  startOfMonth,
  subMonths,
} from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { v4 as uuidv4 } from 'uuid'
import * as XLSX from 'xlsx'
import AutocompleteClienteNode from '../../components/autocomplete-cliente/autocomplete-clientenode'
import Navbar from '../../components/navbar/navbar'
import { useOrdenation } from '../../hooks/useOrdenation'
import apinode from '../../services/apinode'
import { unifyParams } from '../../utils/unifyParams'

// ── helpers ───────────────────────────────────────────────────────────────────
const normalizarFormaPgto = (forma) => {
  if (!forma) return ''
  const lower = forma.toLowerCase().trim()
  if (lower === 'boleto' || lower === 'boleto bancário') return 'Boleto'
  if (['cartao_debito', 'cartão de débito', 'cartão', 'debito'].includes(lower))
    return 'cartao_debito'
  if (['cartao_credito', 'cartão de crédito', 'credito'].includes(lower))
    return 'cartao_credito'
  if (
    [
      'transferencia_bancaria',
      'transferência bancária',
      'transferencia',
    ].includes(lower)
  )
    return 'transferencia_bancaria'
  if (lower === 'cheque') return 'cheque'
  if (lower === 'pix') return 'pix'
  if (lower === 'haver') return 'haver'
  if (lower === 'dinheiro') return 'dinheiro'
  return forma
}

const BRL = (v) =>
  v != null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—'

function toYMD(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

const FORMAS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'transferencia_bancaria', label: 'Transferência Bancária' },
  { value: 'Boleto', label: 'Boleto Bancário' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'pix', label: 'PIX' },
  { value: 'haver', label: 'Haver' },
]

// ── component ─────────────────────────────────────────────────────────────────
export default function AccountTableReceber() {
  const [contas, setContas] = useState([])
  const [filteredContas, setFilteredContas] = useState([])
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [searchTextApi, setSearchTextApi] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedSituacao, setSelectedSituacao] = useState('')
  const [categoryDescs, setCategoryDescs] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [editedConta, setEditedConta] = useState({})
  const [showModal, setShowModal] = useState(false)
  const [nomeCliente, setNomeCliente] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [selectedPagamento, setSelectedPagamento] = useState('')
  const conta = localStorage.idconta
  const navigate = useNavigate()

  async function listarContas() {
    try {
      const r = await apinode.get('/contasreceber/busca', { params: { conta } })
      const sorted = r.data.sort(
        (a, b) => new Date(a.VENCIMENTO) - new Date(b.VENCIMENTO),
      )
      setContas(sorted)
      setFilteredContas(sorted)
    } catch {}
  }

  useEffect(() => {
    apinode
      .get(`/contasreceber/categoria/${conta}`)
      .then((r) => setCategoryDescs(r.data))
      .catch(() => {})
    listarContas()

    const currentDate = new Date()
    const oneMonthBefore = startOfMonth(subMonths(currentDate, 1))
    const oneMonthAfter = endOfMonth(addMonths(currentDate, 1))

    const startDateFormatted = format(oneMonthBefore, 'yyyy-MM-dd')
    const endDateFormatted = format(oneMonthAfter, 'yyyy-MM-dd')

    setStartDate(startDateFormatted)
    setEndDate(endDateFormatted)
  }, [])

  const handleSearch = async () => {
    try {
      setIsSearching(true)

      const url = unifyParams(`/contasreceber/search/${conta}`, [
        {
          key: 'data1',
          value: startDate,
        },
        {
          key: 'data2',
          value: endDate,
        },
        {
          key: 'searchTerm',
          value: searchTextApi,
        },
        {
          key: 'pagamento',
          value: selectedPagamento,
        },
      ])

      const r = await apinode.get(url)

      const sorted = r.data.sort(
        (a, b) => new Date(a.VENCIMENTO) - new Date(b.VENCIMENTO),
      )

      setContas(sorted)
      setFilteredContas(sorted)
    } catch {
      toast.error('Erro na busca')
    } finally {
      setIsSearching(false)
    }
  }

  const handleDelete = (id) => {
    if (!window.confirm('Confirmar exclusão?')) return
    apinode
      .delete(`/contasreceber/deletar/${conta}/${id}`)
      .then(() => {
        toast.success('Deletado!')
        listarContas()
      })
      .catch(() => toast.error('Erro ao deletar'))
  }

  const handleClone = (id) => {
    const acc = contas.find((c) => c.ID === id)
    if (!acc) return
    apinode
      .post(`/contasreceber/addconta/${conta}`, { ...acc, ID: uuidv4() })
      .then(() => {
        toast.success('Clonado!')
        listarContas()
      })
      .catch(() => toast.error('Erro ao clonar'))
  }

  const handleBaixar = async () => {
    if (!selectedAccounts.length) {
      toast.warn('Selecione ao menos uma conta')
      return
    }
    const { value: date } = await Swal.fire({
      title: 'Data de baixa',
      input: 'date',
      confirmButtonText: 'Confirmar',
      confirmButtonColor: '#22c55e',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        Swal.getInput().value = format(new Date(), 'yyyy-MM-dd')
      },
    })
    if (!date) return
    try {
      await apinode.put(`/contasreceber/pgconta/${conta}`, {
        ids: selectedAccounts,
        date,
      })
      toast.success(`${selectedAccounts.length} conta(s) baixada(s)!`)
      setSelectedAccounts([])
      listarContas()
    } catch {
      toast.error('Erro ao baixar')
    }
  }

  const openModal = (account) => {
    setEditedConta({
      ...account,
      VENCIMENTO: toYMD(account.VENCIMENTO),
      VENCIMENTO_ORIGINAL: toYMD(account.VENCIMENTO_ORIGINAL),
      DATA_EMISSAO: toYMD(account.DATA_EMISSAO),
      DATA_PGTO: toYMD(account.DATA_PGTO),
    })
    setNomeCliente(account.PES_RAZAO || '')
    setShowModal(true)
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      await apinode.put(
        `/contasreceber/updateconta/${conta}/${editedConta.ID}`,
        editedConta,
      )
      toast.success('Conta atualizada!')
      setShowModal(false)
      listarContas()
    } catch {
      toast.error('Erro ao salvar')
    } finally {
      setIsSaving(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setEditedConta((prev) => ({ ...prev, [name]: value }))
  }

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(
      filteredAccounts.map((a) => ({
        ID: a.ID,
        Situação: a.SITUACAO,
        Cliente: a.PES_RAZAO,
        Histórico: a.HISTORICO_CONTA,
        Forma: a.FORMA_DE_PGTO,
        Vencimento: new Date(a.VENCIMENTO).toLocaleDateString('pt-BR'),
        Valor: BRL(a.VALOR),
      })),
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Contas')
    XLSX.writeFile(wb, 'ContasReceber.xlsx')
  }

  const filteredAccounts = useMemo(() => {
    let data = filteredContas
    if (selectedSituacao) {
      data = data.filter((a) => {
        if (selectedSituacao === 'aberto')
          return (
            new Date(a.VENCIMENTO) >= new Date() &&
            a.SITUACAO !== 'Pago' &&
            a.SITUACAO !== 'Recebido'
          )
        if (selectedSituacao === 'pago')
          return a.SITUACAO === 'Pago' || a.SITUACAO === 'Recebido'
        if (selectedSituacao === 'vencida')
          return (
            new Date(a.VENCIMENTO) < new Date() &&
            a.SITUACAO !== 'Pago' &&
            a.SITUACAO !== 'Recebido'
          )
        return true
      })
    }
    if (searchTerm) {
      const t = searchTerm.toLowerCase()
      data = data.filter(
        (a) =>
          a.SITUACAO?.toLowerCase().includes(t) ||
          a.PES_RAZAO?.toLowerCase().includes(t) ||
          a.HISTORICO_CONTA?.toLowerCase().includes(t) ||
          a.ID?.toString().includes(t),
      )
    }
    return data
  }, [filteredContas, selectedSituacao, searchTerm])

  const { filteredItems, Th, Paginate, itemsPerPage, setItemsPerPage } =
    useOrdenation(filteredAccounts, 'VENCIMENTO', 'desc', '25')

  const stats = useMemo(() => {
    const isPago = (a) => a.SITUACAO === 'Pago' || a.SITUACAO === 'Recebido'
    const isVenc = (a) => !isPago(a) && new Date(a.VENCIMENTO) < new Date()
    const total = filteredContas.reduce((s, a) => s + (a.VALOR || 0), 0)
    const vPago = filteredContas
      .filter(isPago)
      .reduce((s, a) => s + (a.VALOR || 0), 0)
    const vVenc = filteredContas
      .filter(isVenc)
      .reduce((s, a) => s + (a.VALOR || 0), 0)
    const vAbert = filteredContas
      .filter((a) => !isPago(a) && !isVenc(a))
      .reduce((s, a) => s + (a.VALOR || 0), 0)
    const sel = selectedAccounts.reduce(
      (s, id) => s + (filteredContas.find((c) => c.ID === id)?.VALOR || 0),
      0,
    )
    return {
      total,
      vPago,
      vVenc,
      vAbert,
      sel,
      cTotal: filteredContas.length,
      cPago: filteredContas.filter(isPago).length,
      cVencida: filteredContas.filter(isVenc).length,
      cAberto: filteredContas.filter((a) => !isPago(a) && !isVenc(a)).length,
    }
  }, [filteredContas, selectedAccounts])

  const getCatLabel = (code) => {
    const cat = categoryDescs.find((c) => c.CODIGO === parseInt(code))
    return cat?.DESCRICAO || code
  }

  const categorySums = useMemo(() => {
    const map = {}
    filteredAccounts.forEach((a) => {
      const k = a.CATEGORIA || 'Sem categoria'
      if (!map[k]) map[k] = { code: k, sum: 0 }
      map[k].sum += a.VALOR || 0
    })
    return Object.values(map)
      .sort((a, b) => b.sum - a.sum)
      .slice(0, 6)
  }, [filteredAccounts])

  const getStatus = (a) => {
    const isPago = a.SITUACAO === 'Pago' || a.SITUACAO === 'Recebido'
    const isVenc = !isPago && new Date(a.VENCIMENTO) < new Date()
    if (isPago)
      return { label: a.SITUACAO, cls: 'st-pago', icon: faCheckCircle }
    if (isVenc)
      return { label: 'Vencida', cls: 'st-venc', icon: faExclamationCircle }
    return { label: 'Em Aberto', cls: 'st-aber', icon: faDollarSign }
  }

  return (
    <>
      <Navbar />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .ar{--p:#6366f1;--pg:#8b5cf6;--g:#22c55e;--r:#ef4444;--a:#f97316;--b:#3b82f6;--bg:#f1f5f9;--s:#fff;--s2:#f8fafc;--bd:#e2e8f0;--tx:#0f172a;--mu:#94a3b8;--ra:14px;font-family:'Plus Jakarta Sans',sans-serif;background:var(--bg);min-height:100vh;padding:28px 24px;}
        .ar *{box-sizing:border-box;}
        .ar-inner{max-width:1480px;margin:0 auto;}

        /* topbar */
        .ar-top{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:14px;margin-bottom:28px;}
        .ar-title{font-size:1.75rem;font-weight:800;color:var(--tx);letter-spacing:-.03em;display:flex;align-items:center;gap:10px;}
        .ar-title-icon{width:42px;height:42px;background:linear-gradient(135deg,var(--p),var(--pg));border-radius:12px;display:grid;place-items:center;color:#fff;font-size:1.1rem;}
        .ar-btns{display:flex;gap:8px;flex-wrap:wrap;}

        /* buttons */
        .btn-ar{border:none;border-radius:10px;padding:10px 18px;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;transition:all .18s;display:inline-flex;align-items:center;gap:7px;white-space:nowrap;}
        .btn-ar:disabled{opacity:.45;cursor:not-allowed;transform:none!important;}
        .btn-ar:hover:not(:disabled){transform:translateY(-1px);}
        .btn-p{background:linear-gradient(135deg,var(--p),var(--pg));color:#fff;box-shadow:0 4px 14px rgba(99,102,241,.25);}
        .btn-p:hover:not(:disabled){box-shadow:0 6px 20px rgba(99,102,241,.35);}
        .btn-g{background:linear-gradient(135deg,#16a34a,var(--g));color:#fff;box-shadow:0 4px 14px rgba(34,197,94,.2);}
        .btn-g:hover:not(:disabled){box-shadow:0 6px 20px rgba(34,197,94,.3);}
        .btn-ou{background:var(--s);border:1.5px solid var(--bd);color:#475569;box-shadow:0 2px 6px rgba(0,0,0,.05);}
        .btn-ou:hover:not(:disabled){border-color:var(--p);color:var(--p);}
        .btn-sm{padding:7px 13px;font-size:12px;border-radius:8px;}
        .btn-ic{width:32px;height:32px;padding:0;border-radius:8px;justify-content:center;}

        /* stat cards */
        .ar-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px;}
        @media(max-width:900px){.ar-stats{grid-template-columns:1fr 1fr;}}
        @media(max-width:480px){.ar-stats{grid-template-columns:1fr;}}
        .ar-stat{background:var(--s);border:1px solid var(--bd);border-radius:var(--ra);padding:20px 22px;position:relative;overflow:hidden;transition:transform .18s,box-shadow .18s;}
        .ar-stat:hover{transform:translateY(-2px);box-shadow:0 8px 30px rgba(0,0,0,.08);}
        .ar-stat-icon{width:44px;height:44px;border-radius:12px;display:grid;place-items:center;font-size:1.1rem;margin-bottom:14px;}
        .ar-stat label{display:block;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--mu);margin-bottom:6px;}
        .ar-stat .val{font-size:1.35rem;font-weight:800;color:var(--tx);letter-spacing:-.02em;margin-bottom:2px;}
        .ar-stat .sub{font-size:11px;color:var(--mu);font-weight:500;}
        .s-tot .ar-stat-icon{background:#ede9fe;color:var(--p);}
        .s-ab  .ar-stat-icon{background:#dbeafe;color:var(--b);}
        .s-vc  .ar-stat-icon{background:#fee2e2;color:var(--r);}
        .s-pg  .ar-stat-icon{background:#dcfce7;color:var(--g);}

        /* card */
        .ar-card{background:var(--s);border:1px solid var(--bd);border-radius:var(--ra);margin-bottom:18px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,.04);}
        .ar-card-hd{padding:14px 20px;border-bottom:1px solid var(--bd);display:flex;align-items:center;gap:10px;font-size:13px;font-weight:700;color:var(--tx);}
        .ar-card-hd .ml{margin-left:auto;font-size:11px;font-weight:500;color:var(--mu);}
        .ar-card-bd{padding:20px;}

        /* filters */
        .ar-filters{display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;}
        .ar-field{display:flex;flex-direction:column;gap:5px;}
        .ar-field label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--mu);}
        .ar-inp{background:#f8fafc;border:1.5px solid var(--bd);border-radius:9px;padding:9px 13px;color:var(--tx);font-family:inherit;font-size:13px;font-weight:500;outline:none;transition:border-color .18s,box-shadow .18s;}
        .ar-inp:focus{border-color:var(--p);box-shadow:0 0 0 3px rgba(99,102,241,.1);background:#fff;}
        .ar-inp::placeholder{color:var(--mu);}
        .ar-inp-fl{flex:1;min-width:200px;}

        /* seg tabs */
        .ar-seg{display:flex;background:#f1f5f9;border-radius:10px;padding:4px;gap:2px;margin-bottom:0;}
        .ar-seg-btn{border:none;background:transparent;border-radius:7px;padding:7px 14px;font-family:inherit;font-size:12px;font-weight:600;color:var(--mu);cursor:pointer;transition:all .18s;display:flex;align-items:center;gap:6px;}
        .ar-seg-btn.active{background:var(--s);color:var(--tx);box-shadow:0 2px 8px rgba(0,0,0,.1);}
        .ar-seg-btn .cnt{background:var(--bd);border-radius:99px;padding:1px 7px;font-size:10px;font-weight:700;}
        .ar-seg-btn.active .cnt{background:var(--p);color:#fff;}

        /* sel bar */
        .ar-selbar{background:linear-gradient(135deg,#ede9fe,#dbeafe);border:1.5px solid rgba(99,102,241,.2);border-radius:12px;padding:13px 18px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;margin:14px 20px 0;}
        .ar-selbar-info{display:flex;align-items:center;gap:16px;}
        .ar-sel-cnt{font-size:1rem;font-weight:800;color:var(--p);}
        .ar-sel-val{font-size:13px;color:#475569;}

        /* table */
        .ar-tbl-wrap{overflow-x:auto;}
        .ar-tbl{width:100%;border-collapse:collapse;font-size:13px;}
        .ar-tbl th{background:#f8fafc;padding:11px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--mu);border-bottom:1.5px solid var(--bd);white-space:nowrap;cursor:pointer;user-select:none;transition:background .15s;}
        .ar-tbl th:hover{background:#f1f5f9;color:var(--tx);}
        .ar-tbl td{padding:11px 14px;border-bottom:1px solid #f8fafc;vertical-align:middle;}
        .ar-tbl tr:hover td{background:#fafbff;}
        .ar-tbl tr.tr-venc td{background:#fff5f5;}
        .ar-tbl tr.tr-venc:hover td{background:#fee2e2;}
        .ar-tbl tr.tr-pago td{opacity:.6;}
        .ar-tbl tr.tr-pago:hover td{opacity:.8;}

        /* status chips */
        .chip{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:99px;font-size:11px;font-weight:700;white-space:nowrap;}
        .chip-pg{background:#dcfce7;color:#15803d;}
        .chip-vc{background:#fee2e2;color:#dc2626;}
        .chip-ab{background:#dbeafe;color:#1d4ed8;}

        /* forma tag */
        .forma-tag{background:#ede9fe;color:var(--p);border-radius:6px;padding:3px 8px;font-size:11px;font-weight:600;}

        /* cli cell */
        .cli-name{font-weight:600;color:var(--tx);}
        .cli-sub{font-size:11px;color:var(--mu);margin-top:1px;}

        /* valor */
        .val-pg{color:#16a34a;font-weight:700;}
        .val-vc{color:var(--r);font-weight:700;}
        .val-ab{color:var(--tx);font-weight:600;}

        /* empty */
        .ar-empty{text-align:center;padding:52px 20px;color:var(--mu);}
        .ar-empty-ico{font-size:2.5rem;display:block;margin-bottom:12px;opacity:.4;}

        /* spinner */
        .ar-spin{display:inline-block;width:13px;height:13px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:sn .65s linear infinite;}
        @keyframes sn{to{transform:rotate(360deg);}}

        /* pg bar */
        .ar-pgbar{display:flex;align-items:center;justify-content:space-between;padding:13px 20px;border-top:1px solid var(--bd);flex-wrap:wrap;gap:10px;}
        .ar-pgbar-info{font-size:12px;color:var(--mu);font-weight:500;}
        .ar-pp{display:flex;align-items:center;gap:8px;font-size:12px;color:var(--mu);font-weight:600;}

        /* cat pills */
        .cat-row{display:flex;flex-wrap:wrap;gap:8px;}
        .cat-pill{background:var(--s2);border:1px solid var(--bd);border-radius:8px;padding:6px 12px;font-size:12px;display:flex;align-items:center;gap:8px;}
        .cat-pill-name{color:#475569;font-weight:500;}
        .cat-pill-val{color:var(--p);font-weight:700;}

        /* checkbox */
        input[type=checkbox]{width:16px;height:16px;accent-color:var(--p);cursor:pointer;border-radius:4px;}

        /* modal */
        .modal-content{border-radius:16px!important;border:none!important;box-shadow:0 25px 60px rgba(0,0,0,.15)!important;}
        .modal-header{background:linear-gradient(135deg,var(--p),var(--pg))!important;border:none!important;border-radius:16px 16px 0 0!important;padding:20px 24px!important;}
        .modal-title{color:#fff!important;font-weight:800!important;font-size:1rem!important;}
        .modal-header .btn-close{filter:invert(1)!important;}
        .modal-body{padding:24px!important;}
        .modal-footer{border-top:1px solid var(--bd)!important;padding:16px 24px!important;}
        .modal-body .form-label{font-size:10px!important;font-weight:700!important;text-transform:uppercase!important;letter-spacing:.07em!important;color:var(--mu)!important;margin-bottom:5px!important;}
        .modal-body .form-control,.modal-body .form-select{border:1.5px solid var(--bd)!important;border-radius:9px!important;font-family:inherit!important;font-size:13px!important;background:#f8fafc!important;}
        .modal-body .form-control:focus,.modal-body .form-select:focus{border-color:var(--p)!important;box-shadow:0 0 0 3px rgba(99,102,241,.12)!important;background:#fff!important;}
      `}</style>

      <div className="ar">
        <div className="ar-inner">
          {/* ── Top ── */}
          <div className="ar-top">
            <div className="ar-title">
              <div className="ar-title-icon">
                <i className="bi bi-cash-stack" />
              </div>
              Contas a Receber
            </div>
            <div className="ar-btns">
              <button
                className="btn-ar btn-ou"
                onClick={handleExport}
              >
                <FontAwesomeIcon icon={faFileExcel} /> Exportar
              </button>
              <button
                className="btn-ar btn-g"
                onClick={handleBaixar}
                disabled={!selectedAccounts.length}
              >
                <FontAwesomeIcon icon={faMoneyBillWheat} />
                Baixar{' '}
                {selectedAccounts.length > 0
                  ? `(${selectedAccounts.length})`
                  : ''}
              </button>
              <Link
                to="/contasreceber/novo"
                className="btn-ar btn-p"
                style={{ textDecoration: 'none' }}
              >
                <FontAwesomeIcon icon={faAdd} /> Nova Conta
              </Link>
            </div>
          </div>

          {/* ── Stats ── */}
          <div className="ar-stats">
            <div className="ar-stat s-tot">
              <div className="ar-stat-icon">
                <i className="bi bi-grid-3x3-gap-fill" />
              </div>
              <label>Total de Contas</label>
              <div className="val">{stats.cTotal}</div>
              <div className="sub">{BRL(stats.total)}</div>
            </div>
            <div className="ar-stat s-ab">
              <div className="ar-stat-icon">
                <i className="bi bi-clock" />
              </div>
              <label>Em Aberto</label>
              <div className="val">{stats.cAberto}</div>
              <div className="sub">{BRL(stats.vAbert)}</div>
            </div>
            <div className="ar-stat s-vc">
              <div className="ar-stat-icon">
                <i className="bi bi-exclamation-triangle-fill" />
              </div>
              <label>Vencidas</label>
              <div className="val">{stats.cVencida}</div>
              <div className="sub">{BRL(stats.vVenc)}</div>
            </div>
            <div className="ar-stat s-pg">
              <div className="ar-stat-icon">
                <i className="bi bi-check-circle-fill" />
              </div>
              <label>Pagas / Recebidas</label>
              <div className="val">{stats.cPago}</div>
              <div className="sub">{BRL(stats.vPago)}</div>
            </div>
          </div>

          {/* ── Filtros ── */}
          <div className="ar-card">
            <div className="ar-card-hd">
              <i className="bi bi-sliders" /> Filtros de Busca
            </div>
            <div className="ar-card-bd">
              <div className="ar-filters">
                <div
                  className="ar-field"
                  style={{ flex: 1, minWidth: 220 }}
                >
                  <label>Buscar</label>
                  <input
                    className="ar-inp"
                    placeholder="Cliente, histórico, ID..."
                    value={searchTextApi}
                    onChange={(e) => setSearchTextApi(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>

                <div
                  className="ar-field"
                  style={{ flex: 1, minWidth: 220 }}
                >
                  <label>Pagamento</label>
                  <select
                    className="ar-inp"
                    value={selectedPagamento}
                    onChange={(e) => setSelectedPagamento(e.target.value)}
                  >
                    <option value="">Selecione...</option>

                    {FORMAS.map((o) => (
                      <option
                        key={o.value}
                        value={o.value}
                      >
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ar-field">
                  <label>De</label>
                  <input
                    type="date"
                    className="ar-inp"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="ar-field">
                  <label>Até</label>
                  <input
                    type="date"
                    className="ar-inp"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <button
                  className="btn-ar btn-p"
                  onClick={handleSearch}
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <span className="ar-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )}
                  {isSearching ? 'Buscando...' : 'Buscar'}
                </button>
              </div>

              {/* filtro local + seg */}
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  marginTop: 14,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <input
                  className="ar-inp"
                  style={{ flex: 1, minWidth: 180 }}
                  placeholder="Filtrar na lista..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div className="ar-seg">
                  {[
                    { key: '', label: 'Todas', count: stats.cTotal },
                    { key: 'aberto', label: 'Em Aberto', count: stats.cAberto },
                    {
                      key: 'vencida',
                      label: 'Vencidas',
                      count: stats.cVencida,
                    },
                    { key: 'pago', label: 'Pagas', count: stats.cPago },
                  ].map(({ key, label, count }) => (
                    <button
                      key={key}
                      className={`ar-seg-btn${selectedSituacao === key ? ' active' : ''}`}
                      onClick={() => setSelectedSituacao(key)}
                    >
                      {label} <span className="cnt">{count}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Categorias ── */}
          {categorySums.length > 0 && (
            <div className="ar-card">
              <div className="ar-card-hd">
                <i className="bi bi-pie-chart-fill" /> Por Categoria
              </div>
              <div className="ar-card-bd">
                <div className="cat-row">
                  {categorySums.map(({ code, sum }) => (
                    <div
                      key={code}
                      className="cat-pill"
                    >
                      <span className="cat-pill-name">{getCatLabel(code)}</span>
                      <span className="cat-pill-val">{BRL(sum)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Tabela ── */}
          <div className="ar-card">
            <div className="ar-card-hd">
              <i className="bi bi-table" /> Lançamentos
              <span className="ml">
                {filteredItems.length} de {filteredAccounts.length} registros
              </span>
            </div>

            {/* sel bar */}
            {selectedAccounts.length > 0 && (
              <div className="ar-selbar">
                <div className="ar-selbar-info">
                  <span className="ar-sel-cnt">
                    {selectedAccounts.length} selecionada(s)
                  </span>
                  <span className="ar-sel-val">
                    Soma: <strong>{BRL(stats.sel)}</strong>
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn-ar btn-g btn-sm"
                    onClick={handleBaixar}
                  >
                    <FontAwesomeIcon icon={faMoneyBillWheat} /> Baixar
                    Selecionadas
                  </button>
                  <button
                    className="btn-ar btn-ou btn-sm"
                    onClick={() => setSelectedAccounts([])}
                  >
                    Limpar seleção
                  </button>
                </div>
              </div>
            )}

            {/* control row */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 20px',
                borderBottom: '1px solid #f1f5f9',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    filteredItems.length > 0 &&
                    filteredItems.every((a) => selectedAccounts.includes(a.ID))
                  }
                  onChange={(e) => {
                    if (e.target.checked)
                      setSelectedAccounts(filteredItems.map((a) => a.ID))
                    else setSelectedAccounts([])
                  }}
                />
                Selecionar página
              </label>
              <div className="ar-pp">
                Por página:
                <select
                  className="ar-inp"
                  style={{ padding: '5px 9px', fontSize: 12 }}
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(e.target.value)}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>

            <div className="ar-tbl-wrap">
              <table className="ar-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}></th>
                    <Th
                      label="ID"
                      value="ID"
                    />
                    <Th
                      label="Situação"
                      value="SITUACAO"
                    />
                    <Th
                      label="Cliente"
                      value="PES_RAZAO"
                    />
                    <Th
                      label="Histórico"
                      value="HISTORICO_CONTA"
                    />
                    <Th
                      label="Forma"
                      value="FORMA_DE_PGTO"
                    />
                    <Th
                      label="Vencimento"
                      value="VENCIMENTO"
                    />
                    <Th
                      label="Valor"
                      value="VALOR"
                    />
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((a) => {
                    const isPago =
                      a.SITUACAO === 'Pago' || a.SITUACAO === 'Recebido'
                    const isVenc =
                      !isPago && new Date(a.VENCIMENTO) < new Date()
                    const st = getStatus(a)
                    return (
                      <tr
                        key={a.ID}
                        className={isPago ? 'tr-pago' : isVenc ? 'tr-venc' : ''}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedAccounts.includes(a.ID)}
                            onChange={() =>
                              setSelectedAccounts((prev) =>
                                prev.includes(a.ID)
                                  ? prev.filter((x) => x !== a.ID)
                                  : [...prev, a.ID],
                              )
                            }
                          />
                        </td>
                        <td
                          style={{
                            fontWeight: 700,
                            color: '#6366f1',
                            fontSize: 12,
                          }}
                        >
                          #{a.ID}
                        </td>
                        <td>
                          <span
                            className={`chip ${st.cls === 'st-pago' ? 'chip-pg' : st.cls === 'st-venc' ? 'chip-vc' : 'chip-ab'}`}
                          >
                            <FontAwesomeIcon icon={st.icon} /> {st.label}
                          </span>
                        </td>
                        <td>
                          <div className="cli-name">{a.PES_RAZAO || '—'}</div>
                          <div className="cli-sub">ID {a.ID_FORNCEDOR}</div>
                        </td>
                        <td
                          style={{
                            maxWidth: 200,
                            textWrap: 'wrap',
                            overflow: 'hidden',
                            // textOverflow: 'ellipsis',
                            // whiteSpace: 'nowrap',
                            color: '#64748b',
                          }}
                        >
                          {a.HISTORICO_CONTA}
                        </td>
                        <td>
                          {a.FORMA_DE_PGTO && (
                            <span className="forma-tag">{a.FORMA_DE_PGTO}</span>
                          )}
                        </td>
                        <td
                          style={{
                            fontWeight: 600,
                            color: isVenc ? '#dc2626' : '#475569',
                          }}
                        >
                          {new Date(a.VENCIMENTO).toLocaleDateString('pt-BR')}
                          {isVenc && (
                            <div
                              style={{
                                fontSize: 10,
                                color: '#ef4444',
                                fontWeight: 700,
                                marginTop: 1,
                              }}
                            >
                              VENCIDA
                            </div>
                          )}
                        </td>
                        <td
                          className={
                            isPago ? 'val-pg' : isVenc ? 'val-vc' : 'val-ab'
                          }
                        >
                          {BRL(a.VALOR)}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn-ar btn-sm btn-ic"
                              style={{
                                background: '#fef3c7',
                                color: '#d97706',
                              }}
                              onClick={() =>
                                navigate(`/contasreceber/editar/${a.ID}`)
                              }
                              title="Editar"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="btn-ar btn-sm btn-ic"
                              style={{
                                background: '#fee2e2',
                                color: '#dc2626',
                              }}
                              onClick={() => handleDelete(a.ID)}
                              title="Excluir"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                            <button
                              className="btn-ar btn-sm btn-ic"
                              style={{
                                background: '#ede9fe',
                                color: '#7c3aed',
                              }}
                              onClick={() => handleClone(a.ID)}
                              title="Clonar"
                            >
                              <FontAwesomeIcon icon={faClone} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}

                  {filteredItems.length === 0 && (
                    <tr>
                      <td colSpan={9}>
                        <div className="ar-empty">
                          <span className="ar-empty-ico">📭</span>
                          Nenhuma conta encontrada
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="ar-pgbar">
              <span className="ar-pgbar-info">
                {filteredItems.length} de {filteredAccounts.length} registros
              </span>
              <Paginate />
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal Edição ── */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar Conta #{editedConta?.ID}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Label>Cliente</Form.Label>
            <AutocompleteClienteNode
              placeholder="Pesquisar cliente..."
              onClickNome={(nome) => {
                setNomeCliente(nome)
                setEditedConta((p) => ({ ...p, PES_RAZAO: nome }))
              }}
              onClickId={(id) =>
                setEditedConta((p) => ({ ...p, ID_FORNCEDOR: id }))
              }
              onClickCpf={(cpf) =>
                setEditedConta((p) => ({ ...p, PES_CNPJ_CPF: cpf }))
              }
              value={nomeCliente}
              className="form-control"
            />
          </div>
          <div className="row mb-3">
            <div className="col-md-3">
              <Form.Label>ID</Form.Label>
              <input
                disabled
                type="text"
                className="form-control"
                value={editedConta?.ID || ''}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>ID Pedido / NFe</Form.Label>
              <input
                type="text"
                className="form-control"
                name="IDPEDIDOCOMPRA"
                value={editedConta?.IDPEDIDOCOMPRA || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-5">
              <Form.Label>Forma de Pagamento</Form.Label>
              <select
                className="form-select"
                name="FORMA_DE_PGTO"
                value={normalizarFormaPgto(editedConta?.FORMA_DE_PGTO)}
                onChange={handleInputChange}
              >
                <option value="">Selecione...</option>
                {FORMAS.map((o) => (
                  <option
                    key={o.value}
                    value={o.value}
                  >
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-4">
              <Form.Label>Vencimento</Form.Label>
              <input
                type="date"
                className="form-control"
                name="VENCIMENTO"
                value={editedConta?.VENCIMENTO || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Valor</Form.Label>
              <input
                type="text"
                className="form-control"
                name="VALOR"
                value={editedConta?.VALOR || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Situação</Form.Label>
              <select
                className="form-select"
                name="SITUACAO"
                value={editedConta?.SITUACAO || ''}
                onChange={handleInputChange}
              >
                <option value="Em Aberto">Em Aberto</option>
                <option value="Pago">Pago</option>
                <option value="Recebido">Recebido</option>
                <option value="Aguardando Pagamento">
                  Aguardando Pagamento
                </option>
                <option value="Vencida">Vencida</option>
                <option value="Cartorio">Cartório</option>
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Label>Nº Identificação</Form.Label>
              <input
                type="text"
                className="form-control"
                name="NUMERO_IDENT"
                value={editedConta?.NUMERO_IDENT || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Nº Documento</Form.Label>
              <input
                type="text"
                className="form-control"
                name="NUMERO_DOC"
                value={editedConta?.NUMERO_DOC || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Label>Vencimento Original</Form.Label>
              <input
                type="date"
                className="form-control"
                name="VENCIMENTO_ORIGINAL"
                value={editedConta?.VENCIMENTO_ORIGINAL || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Data de Pagamento</Form.Label>
              <input
                type="date"
                className="form-control"
                name="DATA_PGTO"
                value={editedConta?.DATA_PGTO || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Label>Portador</Form.Label>
              <input
                type="text"
                className="form-control"
                name="PORTADOR"
                value={editedConta?.PORTADOR || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Ocorrência</Form.Label>
              <input
                type="text"
                className="form-control"
                name="OCORRENCIA"
                value={editedConta?.OCORRENCIA || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="mb-0">
            <Form.Label>Histórico da Conta</Form.Label>
            <textarea
              className="form-control"
              name="HISTORICO_CONTA"
              rows={3}
              value={editedConta?.HISTORICO_CONTA || ''}
              onChange={handleInputChange}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
          >
            Fechar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving}
            style={{
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              border: 'none',
            }}
          >
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
